<script lang="ts">
export default {
  name: 'UIInput',
};
</script>

<script setup lang="ts">
import { ref, useSlots, watch } from 'vue';
import { useField } from 'vee-validate';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { maska } from 'maska';

import type Maska from 'maska/types/maska';

export interface Props {
  name: string;
  label?: string;
  disabled?: boolean;
  type?: string;
  theme?: 'outline' | 'solo';
  placeholder?: string;
  modelValue?: string | number | null | boolean;
  togglePassword?: boolean;
  min?: number;
  max?: number;
  autocomplete?: string;
  required?: boolean;
  pattern?: string;
  validateOnValueUpdate?: boolean;
  ignoreValidation?: boolean;
  iban?: boolean;
  mask?: Maska['_opts'];
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  type: 'text',
  theme: 'outline',
  placeholder: '',
  modelValue: '',
  togglePassword: false,
  disabled: false,
  min: 0,
  max: 0,
  autocomplete: 'off',
  pattern: undefined,
  required: undefined,
  validateOnValueUpdate: false,
  ignoreValidation: false,
  iban: false,
  mask: undefined,
});

const emit = defineEmits(['update:modelValue']);

const {
  errorMessage,
  value: inputValue,
  meta,
  setValue,
  handleChange,
} = useField(props.name, undefined, {
  initialValue: props.modelValue,
  validateOnValueUpdate: props.validateOnValueUpdate,
});

const vMaska = maska;

const slots = useSlots();
const inputType = ref(props.type);
const ibanMask = { mask: 'AAXX XXXX XXXX XXXX XXXX XXXX XXXX XXXX' };

const focus = () => {
  const id = slugify(props.name);

  if (id) { document.getElementById(id)?.focus() }
};

const handleInput = async (event: Event) => {
  if (props.disabled) {
    event.preventDefault();
    inputValue.value = props.modelValue;
    return;
  }

  handleChange(event);
  emit('update:modelValue', props.iban ? (inputValue.value as string).toUpperCase() : inputValue.value );
};

watch(() => props.modelValue, value => { setValue(value); });

defineExpose({ focus });
</script>

<template>
  <div class="w-full">
    <label v-if="props.label" class="mb-0.5 inline-block" :for="slugify(name)">{{ props.label }}</label>
    <div class="relative inline-block w-full">
      <span v-if="slots['icon-start']" class="absolute top-3 left-3 flex items-center">
        <slot name="icon-start" />
      </span>
      <input
        :id="slugify(name)"
        v-model="inputValue"
        v-maska="iban ? ibanMask : mask ?? undefined"
        :data-test-id="slugify(name)"
        :type="inputType"
        :name="name"
        :placeholder="placeholder"
        :disabled="disabled"
        :min="min"
        :max="max"
        :autocomplete="autocomplete"
        :pattern="pattern"
        :required="required"
        :class="{
          // eslint-disable-next-line max-len
          ['!bg-orange-100 !border-orange-900 !text-orange-900 shadow-activeError focus:!border-blue-300 focus:!text-gray-900 focus:!bg-white']: errorMessage,
          ['!border-green-700 focus:!shadow-activeSuccess']: meta.dirty && meta.valid && !ignoreValidation,
          ['pl-12']: slots['icon-start'],
          ['pr-10']: slots['icon-end'] || !ignoreValidation,
          // eslint-disable-next-line max-len
          ['border border-blue-300 rounded-lg focus:shadow-active transition-all duration-300 disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-700']:
            props.theme === 'outline',
          // eslint-disable-next-line max-len
          ['shadow-element focus:shadow-active rounded-lg bg-white disabled:bg-gray-100 disabled:border-gray-100 disabled:text-gray-700']: props.theme === 'solo',
          ['uppercase']: iban,
        }"
        class="peer block w-full bg-white p-2"
        @blur="handleInput"
      />
      <FontAwesomeIcon
        v-if="!ignoreValidation"
        class="absolute top-3 right-4 text-green-700 opacity-0 transition-opacity duration-300"
        :class="{
          ['!opacity-100']: meta.valid
        }"
        :icon="['fas', 'check']"
        size="lg"
      />
      <FontAwesomeIcon
        v-if="togglePassword && inputType === 'password'"
        class="absolute top-3 right-4"
        :icon="['fad', 'eye']"
        size="lg"
        title="Bekijk wachtwoord"
        :data-test-id="`${name}-show-password`"
        @click="inputType = 'text'"
      />
      <FontAwesomeIcon
        v-if="togglePassword && inputType === 'text'"
        class="absolute top-3 right-4"
        :icon="['fad', 'eye-slash']"
        size="lg"
        title="Verberg wachtwoord"
        :data-test-id="`${name}-hide-password`"
        @click="inputType = 'password'"
      />
      <span v-if="slots['icon-end']" class="absolute right-3 flex h-full items-center">
        <slot name="icon-end" />
      </span>
      <span
        class="text-sm text-orange-900 opacity-0 peer-focus:opacity-0"
        :class="{
          ['opacity-100']: errorMessage
        }"
        v-text="errorMessage ?? 'error'"
      />
    </div>
  </div>
</template>
